/** Onboarding / unlock — SPEC §8.3 identity + vault */

import {
  initCrypto,
  WasmIdentity,
  wrapVaultKey,
  unlockVaultKey,
  rewrapVaultKey,
  vaultNeedsRewrap,
  toHex,
  fromHex,
} from "@veto/crypto-bridge";
import { setEventIdPrefix, type Account, type AssuranceMode } from "@veto/domain";
import type { ProfileMedia } from "@veto/protocol";
import {
  addSecurityEvent,
  getAccount,
  getVault,
  saveAccount,
  saveVault,
  setMeta,
  getMeta,
} from "@veto/storage";
import { clearWrappedVault, createNativeVaultBridge } from "../nativeVault";
import { decryptUtf8, stripVaultKeyFromWrapped } from "../vaultCrypto";
import { SessionStore } from "./sessionStore";

let liveIdentity: WasmIdentity | null = null;

export function createLiveIdentity(): WasmIdentity {
  // Free any prior identity so WASM key material is not duplicated in memory.
  clearLiveIdentity();
  liveIdentity = new WasmIdentity();
  return liveIdentity;
}

/** SEC: release WASM key material on vault lock. WASM memory is not GC'd otherwise. */
export function clearLiveIdentity(): void {
  if (liveIdentity) {
    try {
      liveIdentity.free();
    } catch {
      /* already freed */
    }
    liveIdentity = null;
  }
}

/** SEC: free all live ratchet sessions + identity on lock. */
export function destroySessionBundle(bundle: SessionBundle | null): void {
  if (!bundle) {
    clearLiveIdentity();
    return;
  }
  bundle.store.close();
  // Identity is module-singleton; free only if it is this bundle's identity.
  if (bundle.identity === liveIdentity) clearLiveIdentity();
  else {
    try {
      bundle.identity.free();
    } catch {
      /* ignore */
    }
  }
}

export type SessionBundle = {
  identity: WasmIdentity;
  /** Protocol-v2 session records (AKE + Double Ratchet), serialized per chat. */
  store: SessionStore;
};

function bundleFor(identity: WasmIdentity): SessionBundle {
  setEventIdPrefix(toHex(identity.messagingPublicKey()));
  return { identity, store: new SessionStore(identity, identity.messagingPublicKey()) };
}

function looksLikeSealedIdentity(blob: string): boolean {
  try {
    const o = JSON.parse(blob) as { version?: unknown; nonce?: unknown; ciphertext?: unknown };
    return typeof o.version === "number" && o.nonce != null && o.ciphertext != null;
  } catch {
    return false;
  }
}

export async function runOnboarding(input: {
  displayName: string;
  pin: string;
  assuranceMode: AssuranceMode;
  avatar?: ProfileMedia | null;
}): Promise<{ account: Account; vaultKeyB64: string; session: SessionBundle; toast: string }> {
  await initCrypto();
  if ((await getVault()) || (await getAccount())) {
    // Defence in depth: onboarding must never replace an existing identity.
    throw new Error("An account already exists on this device — unlock it instead");
  }
  const identity = createLiveIdentity();
  const msgPk = identity.messagingPublicKey();
  const wrappedRaw = wrapVaultKey(input.pin);
  const { durableWrappedJson, vaultKeyB64: vk } = stripVaultKeyFromWrapped(wrappedRaw);
  if (!vk) throw new Error("vault key missing from wrap");
  await saveVault(durableWrappedJson);
  const nativeVault = await createNativeVaultBridge();
  await nativeVault.setWrappedVault(durableWrappedJson);

  // AUDIT-BRIDGE-001: seal inside WASM — seeds never enter JS as plaintext hex.
  await setMeta("identity.export.enc", identity.sealExport(vk));

  const account: Account = {
    rootId: toHex(msgPk),
    protocolVersion: 1,
    assuranceMode: input.assuranceMode,
    displayName: input.displayName,
    about: "",
    avatar: input.avatar ?? null,
    createdAt: Date.now(),
    messagingPublicKeyHex: toHex(msgPk),
    hybridPublicKeyHex: toHex(identity.hybridPublicKey()),
  };
  await saveAccount(account);
  return {
    account,
    vaultKeyB64: vk,
    session: bundleFor(identity),
    toast: "You're all set — every chat is end-to-end encrypted",
  };
}

/** Same minimum as onboarding (Onboarding.tsx `passcodeStrength`) and the phone app. */
export const MIN_PASSCODE_LENGTH = 6;

/**
 * Change the app passcode (phone parity, SEC-STOR): the current passcode must open the stored
 * wrap; the SAME vault key is then wrapped under the new passcode and replaces the old wrap.
 * Messages and keys are not re-encrypted — only the key that protects them is re-wrapped.
 * Returns null on success, or a message for the person. Passcodes are never logged or kept.
 */
export async function changePasscode(
  current: string,
  next: string,
  confirm: string,
): Promise<string | null> {
  if (next !== confirm) return "The new passcodes don't match";
  if (next.length < MIN_PASSCODE_LENGTH) return `Use at least ${MIN_PASSCODE_LENGTH} characters`;
  await initCrypto();
  const vault = await getVault();
  if (!vault) return "There is no passcode on this device yet";
  let vk: string;
  try {
    vk = unlockVaultKey(vault.wrappedJson, current);
  } catch {
    return "The current passcode is wrong";
  }
  if (next === current) return null;
  const rewrapped = rewrapVaultKey(vk, next);
  // Never replace the only copy with a wrap that doesn't open: prove it first.
  if (unlockVaultKey(rewrapped, next) !== vk) {
    throw new Error("Couldn't change the passcode — nothing was changed");
  }
  // One IndexedDB put: the old wrap is replaced atomically (it is the source of truth for unlock).
  await saveVault(rewrapped);
  // The SecureVault mirror must not keep a copy that still opens with the OLD passcode.
  try {
    const nativeVault = await createNativeVaultBridge();
    await nativeVault.setWrappedVault(rewrapped);
  } catch {
    await clearWrappedVault();
  }
  return null;
}

export async function runUnlock(pin: string): Promise<{
  account: Account;
  vaultKeyB64: string;
  session: SessionBundle;
} | null> {
  await initCrypto();
  const acc = await getAccount();
  const vault = await getVault();
  if (!acc || !vault) return null;
  let vk: string;
  try {
    vk = unlockVaultKey(vault.wrappedJson, pin);
  } catch {
    throw new Error("Incorrect PIN");
  }
  // Upgrade older, cheaper Argon2 wraps in place (same vault key → data unchanged).
  try {
    if (vaultNeedsRewrap(vault.wrappedJson)) {
      const upgraded = rewrapVaultKey(vk, pin);
      await saveVault(upgraded);
      const nativeVault = await createNativeVaultBridge();
      await nativeVault.setWrappedVault(upgraded);
    }
  } catch {
    /* keep the working wrap; retry on next unlock */
  }
  const encExport = await getMeta("identity.export.enc");
  let identity: WasmIdentity;
  // Defensive: never hold two live identities at once.
  clearLiveIdentity();
  if (encExport) {
    if (looksLikeSealedIdentity(encExport)) {
      identity = WasmIdentity.fromSealedExport(vk, encExport);
    } else {
      // Legacy WebCrypto AES-GCM hex blob — migrate to WASM sealExport.
      const raw = JSON.parse(await decryptUtf8(vk, encExport)) as {
        seedsHex: string;
        hybridSecHex: string;
        hybridPubHex: string;
      };
      identity = WasmIdentity.fromExport(
        fromHex(raw.seedsHex),
        fromHex(raw.hybridSecHex),
        fromHex(raw.hybridPubHex),
      );
      await setMeta("identity.export.enc", identity.sealExport(vk));
    }
    liveIdentity = identity;
  } else {
    // Never mint a replacement identity here: the account row, contacts and
    // every peer's copy of our keys would silently stop matching (identity loss
    // disguised as success). Surface it so the user can restore a backup.
    throw new Error("Your identity keys are missing from this device — restore a backup or reset");
  }
  let account = acc;
  const liveEd = toHex(identity.messagingPublicKey());
  if (liveEd !== acc.messagingPublicKeyHex.toLowerCase()) {
    // Repair accounts damaged by the old silent identity regeneration: the live
    // keys are the only ones we can decrypt with, so the account must match them.
    account = {
      ...acc,
      messagingPublicKeyHex: liveEd,
      hybridPublicKeyHex: toHex(identity.hybridPublicKey()),
    };
    await saveAccount(account);
    await addSecurityEvent({
      id: `identity.repair.${liveEd.slice(0, 16)}`,
      kind: "key_change",
      detail:
        "Your account keys were regenerated by an earlier version of Veto. Share your new invite link with your contacts so they can reach you again.",
    });
  }
  return {
    account,
    vaultKeyB64: vk,
    session: bundleFor(identity),
  };
}
